/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { useForm, useFormContext, useWatch } from '@repo/shared-client/form'
import { useCallback, useEffect } from 'react'

import type { PlaceListFilterFormValues } from './place-list-filter-types'

export function usePlaceListFilterForm(filterDefaults: PlaceListFilterFormValues) {
  const formMethods = useForm<PlaceListFilterFormValues>({
    defaultValues: filterDefaults,
  })

  useEffect(() => {
    formMethods.reset(filterDefaults, { keepDirtyValues: true })
  }, [filterDefaults, formMethods])

  return formMethods
}

export function usePlaceListFilterFields(onFiltersApply: (values: PlaceListFilterFormValues) => void) {
  const { getValues } = useFormContext<PlaceListFilterFormValues>()

  const triggerFilterChange = useCallback(
    (patch?: Record<string, unknown>) => {
      onFiltersApply({ ...getValues(), ...patch } as PlaceListFilterFormValues)
    },
    [getValues, onFiltersApply]
  )

  const distance = useWatch({ name: 'distance' })

  const handleSearchChange = useCallback(
    (value: string) => {
      triggerFilterChange({ name: value })
    },
    [triggerFilterChange]
  )

  return { triggerFilterChange, distance, handleSearchChange }
}
